const DOMAIN_BASE_COLOR_MAP = {
  '분자·유전체 (Molecular & Genomic)': '#2563eb',
  '측정·영상 (Measurement & Imaging)': '#059669',
  '임상·문서 (Clinical & Documentary)': '#7c3aed',
  미입력: '#94a3b8',
};

const DATATARGET_FIXED_COLOR_MAP = {
  '사람 (Human)': '#dc2626',
  '동물 (Animal)': '#f97316',
  '세포 / 오가노이드 (Cell / Organoid)': '#b45309',
  '한약재 / 기원식물 (Medicinal Herb / Botanical Origin)': '#eab308',
  '의료기기 (Medical Device)': '#7c2d12',
  '기타 (Other)': '#4b5563',
  미입력: '#cbd5e1',
};

const DATATYPE_FIXED_COLOR_MAP = {
  '오믹스 (Omics)': '#2563eb',
  'DNA 마커 (DNA Marker)': '#3b82f6',
  '화합물·독성 (Compound & Toxicity)': '#60a5fa',

  '의료영상 (Medical Imaging)': '#059669',
  '생체신호 (Biosignal)': '#10b981',
  '체외진단 (In Vitro Diagnostics, IVD)': '#34d399',
  '기기성능 (Device Performance)': '#6ee7b7',

  '임상시험 (Clinical Trial)': '#7c3aed',
  '이상반응 (Adverse Event)': '#8b5cf6',
  'PRO·설문 (PRO & Survey)': '#a78bfa',
  '진단·치료AI (Diagnostic / Therapeutic AI)': '#c084fc',
  'DTx·앱로그 (DTx & App Log)': '#d8b4fe',
  '표준·DB (Standard & Database)': '#e9d5ff',

  '기타 (Other)': '#cbd5e1',
  미입력: '#cbd5e1',
};

export const normalizeLabel = (value) => String(value || '').trim();

const hashString = (str) => {
  const s = normalizeLabel(str);
  let hash = 0;

  for (let i = 0; i < s.length; i += 1) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }

  return Math.abs(hash);
};

const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;

  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  const toHex = (x) =>
    Math.round(255 * x)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
};

const hexToRgb = (hex) => {
  const clean = normalizeLabel(hex).replace('#', '');
  const value =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;

  const num = parseInt(value, 16);

  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const rgbToHsl = ({ r, g, b }) => {
  const nr = r / 255;
  const ng = g / 255;
  const nb = b / 255;

  const max = Math.max(nr, ng, nb);
  const min = Math.min(nr, ng, nb);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;

  switch (max) {
    case nr:
      h = (ng - nb) / d + (ng < nb ? 6 : 0);
      break;
    case ng:
      h = (nb - nr) / d + 2;
      break;
    default:
      h = (nr - ng) / d + 4;
      break;
  }

  h /= 6;

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

export const getStableColorFromLabel = (
  label,
  { hueOffset = 0, saturationRange = [55, 70], lightnessRange = [42, 58] } = {}
) => {
  const raw = normalizeLabel(label);

  if (!raw || raw === '미입력') return '#cbd5e1';

  const hash = hashString(raw);
  const hue = ((hash % 360) + hueOffset) % 360;
  const saturation = saturationRange[0] + (hash % (saturationRange[1] - saturationRange[0] + 1));
  const lightness = lightnessRange[0] + ((hash >> 3) % (lightnessRange[1] - lightnessRange[0] + 1));

  return hslToHex(hue, saturation, lightness);
};

export const getDomainColor = (label) => {
  const raw = normalizeLabel(label);

  if (DOMAIN_BASE_COLOR_MAP[raw]) return DOMAIN_BASE_COLOR_MAP[raw];

  return getStableColorFromLabel(raw, {
    saturationRange: [58, 68],
    lightnessRange: [42, 52],
  });
};

export const getDataTargetColor = (label) => {
  const raw = normalizeLabel(label);

  if (DATATARGET_FIXED_COLOR_MAP[raw]) return DATATARGET_FIXED_COLOR_MAP[raw];

  return getStableColorFromLabel(raw, {
    hueOffset: 12,
    saturationRange: [58, 72],
    lightnessRange: [44, 58],
  });
};

export const getDataTypeColor = (label) => {
  const raw = normalizeLabel(label);

  if (DATATYPE_FIXED_COLOR_MAP[raw]) return DATATYPE_FIXED_COLOR_MAP[raw];

  return getStableColorFromLabel(raw, {
    saturationRange: [50, 65],
    lightnessRange: [46, 60],
  });
};

export const getDataTypeColorByDomain = (domainLabel, dataTypeLabel) => {
  const domain = normalizeLabel(domainLabel);
  const dataType = normalizeLabel(dataTypeLabel);

  if (!dataType || dataType === '미입력') {
    return '#cbd5e1';
  }

  // '기타 (Other)' 고정 색상 적용 - 다른 색상으로 표시
  if (dataType.includes('기타')) {
    return '#4b5563'; // 회색이 아닌 다른 색상
  }

  if (DATATYPE_FIXED_COLOR_MAP[dataType]) {
    return DATATYPE_FIXED_COLOR_MAP[dataType];
  }

  const domainColor = getDomainColor(domain);
  const { h } = rgbToHsl(hexToRgb(domainColor));
  const hash = hashString(`${domain}::${dataType}`);

  const saturation = 52 + (hash % 14);
  const lightness = 42 + ((hash >> 2) % 18);

  return hslToHex(h, saturation, lightness);
};
